import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Globe2 } from "lucide-react";
import PasswordStrength from "@/components/PasswordStrength";

export default function Register() {
  const { register, getApiErrorMessage } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", organization: "",
    country: "", phone: "", role: "partner",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register(form);
      navigate(user.role === "partner" ? "/partner" : "/portal", { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F2] px-6 py-10">
      <div className="w-full max-w-lg bg-white border border-[#E8E6E1] rounded-3xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-xl bg-[#2A4D3E] flex items-center justify-center">
            <Globe2 size={18} className="text-white" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-heading font-semibold text-[15px] text-[#2A332C] leading-tight">AIMS Healthcare</div>
            <div className="text-[10px] text-[#8E9992] uppercase tracking-[0.18em]">IPMP — Partner Onboarding</div>
          </div>
        </div>

        <div className="text-xs uppercase tracking-[0.2em] text-[#8E9992] font-semibold mb-2">Become a referral partner</div>
        <h1 className="font-heading text-3xl font-semibold text-[#2A332C] tracking-tight">Join AIMS</h1>
        <p className="text-sm text-[#5C6660] mt-2 mb-6">Refer international patients and earn commission on every conversion.</p>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full Name" name="name" value={form.name} onChange={onChange} required testid="reg-name" />
            <Field label="Organization" name="organization" value={form.organization} onChange={onChange} testid="reg-org" />
          </div>
          <Field label="Email" type="email" name="email" value={form.email} onChange={onChange} required testid="reg-email" />
          <div>
            <label className="block text-sm font-medium text-[#5C6660] mb-1.5">Password (min 8 chars, mix of cases & numbers)</label>
            <input type="password" name="password" value={form.password} onChange={onChange} required minLength={8} data-testid="reg-password"
              className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E] transition" />
            <PasswordStrength password={form.password} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Country" name="country" value={form.country} onChange={onChange} testid="reg-country" />
            <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="reg-phone" />
          </div>

          {error && (
            <div className="text-sm text-[#C0392B] bg-[#FDEDEC] border border-[#F5D6D2] rounded-xl px-4 py-3" data-testid="register-error">{error}</div>
          )}

          <button
            type="submit" disabled={loading} data-testid="register-submit-button"
            className="w-full bg-[#2A4D3E] text-white rounded-full px-6 py-3.5 font-medium transition-all hover:bg-[#1E3B2F] hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2 group"
          >
            {loading ? "Creating…" : (<>Create account <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>)}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-[#5C6660]">
          Already have an account?{" "}
          <Link to="/login" className="text-[#2A4D3E] font-semibold hover:underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#5C6660] mb-1.5">{label}</label>
      <input
        {...rest} data-testid={testid}
        className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E] transition"
      />
    </div>
  );
}
